import React, { useState, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    borders, colors, space, radii, mediaQueries
} from 'style/config';
import Empty from 'constants/empty';
import actions from 'Actions';
import snbApi from 'services/api/search-n-browse';
import BundleHeader from 'components/ProductPage/PdpBundling/BundleWidget/BundleHeader';
import BundleProductCard from 'components/ProductPage/PdpBundling/BundleWidget/BundleProductCard';
import PriceTotal from 'components/ProductPage/PdpBundling/BundleWidget/PriceTotal';
import AddAllButton from 'components/ProductPage/PdpBundling/BundleWidget/AddAllButton';
import useBundleRecommendations from 'components/ProductPage/PdpBundling/BundleWidget/hooks/useBundleRecommendations';
import { getChangeOptionNoun } from 'components/ProductPage/PdpBundling/BundleWidget/utils/buildBundleProducts';
import localeUtils from 'utils/LanguageLocale';
import BundleWidgetEN from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_en_US';
import BundleWidgetFR from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_fr_CA';

const BUNDLE_DATA_SOURCE = {
    Manual: 'Manual',
    Automated: 'Automated',
    Off: 'Off'
};

const CAROUSEL_CARD_WIDTH = 210;

const getText = localeUtils.getLocalisedDataFromResource(BundleWidgetEN, BundleWidgetFR);

const BundleWidget = ({ currentProduct }) => {
    // TODO FER-790: field names will change when BE completes FER-586.
    const {
        productBundleDataSource, useItWithTitle, ancillarySkus = Empty.Array, currentSku, productDetails
    } = currentProduct || Empty.Object;

    const isAutomated = productBundleDataSource === BUNDLE_DATA_SOURCE.Automated;

    const { bundleProducts: automatedBundleProducts } = useBundleRecommendations({
        isAutomated,
        productId: productDetails?.productId
    });

    const bundleProducts = useMemo(() => {
        if (isAutomated) {
            return automatedBundleProducts;
        }

        if (productBundleDataSource === BUNDLE_DATA_SOURCE.Manual) {
            return ancillarySkus
                .filter(sku => !sku.isOutOfStock)
                .map(sku => (sku.variationType ? { ...sku, variationType: getChangeOptionNoun(sku.variationType) } : sku));
        }

        return Empty.Array;
    }, [isAutomated, automatedBundleProducts, productBundleDataSource, ancillarySkus]);

    const isCarousel = 1 + bundleProducts.length > 4;
    const dispatch = useDispatch();
    const [deselectedSkuIds, setDeselectedSkuIds] = useState(new Set());
    const [selectedSkus, setSelectedSkus] = useState({});
    const isLoading = useRef(false);

    const handleChangeSkuClick = async sku => {
        if (isLoading.current) {
            return;
        }

        isLoading.current = true;

        const swapCallback = newCurrentSku => {
            setSelectedSkus(prev => ({ ...prev, [sku.skuId]: newCurrentSku }));
        };

        const product = await snbApi.getProductDetailsLite(sku.productId, sku.skuId).catch(() => undefined);
        isLoading.current = false;

        dispatch(
            actions.showBundleChangeSkuModal({
                isOpen: true,
                skuToEdit: {
                    ...sku,
                    variationType: product?.variationType ?? sku.variationType,
                    skuSelectorType: product?.skuSelectorType ?? sku.skuSelectorType,
                    currentSku: selectedSkus[sku.skuId] ?? product?.currentSku ?? sku,
                    regularChildSkus: [...(product?.regularChildSkus ?? Empty.Array), ...(product?.onSaleChildSkus ?? Empty.Array)]
                },
                bundleSkuSwapCallback: swapCallback
            })
        );
    };

    if (!bundleProducts.length) {
        return null;
    }

    const handleCheckboxChange = skuId => {
        setDeselectedSkuIds(prev => {
            const next = new Set(prev);
            next.has(skuId) ? next.delete(skuId) : next.add(skuId);

            return next;
        });
    };

    const selectedBundleProducts = bundleProducts
        .filter(sku => !deselectedSkuIds.has(sku.skuId))
        .map(sku => (selectedSkus[sku.skuId] ? { ...sku, ...selectedSkus[sku.skuId] } : sku));
    const hasSelection = selectedBundleProducts.length > 0;
    const bundleTitle = isAutomated ? getText('frequentlyBoughtTogether') : useItWithTitle;

    return (
        <section css={styles.container}>
            <BundleHeader title={bundleTitle} />
            <div css={styles.widgetCard}>
                <ul
                    css={[styles.productsWrapper, isCarousel && styles.productsWrapperCarousel]}
                    aria-label={getText('bundleProductOptions')}
                >
                    <li>
                        <BundleProductCard
                            product={currentSku}
                            isCurrentProduct
                        />
                    </li>
                    {bundleProducts.map(sku => {
                        const displayProduct = selectedSkus[sku.skuId] ? { ...sku, ...selectedSkus[sku.skuId] } : sku;

                        return (
                            <li key={sku.skuId}>
                                <BundleProductCard
                                    product={displayProduct}
                                    isChecked={!deselectedSkuIds.has(sku.skuId)}
                                    onCheckboxChange={() => handleCheckboxChange(sku.skuId)}
                                    onChangeSkuClick={() => handleChangeSkuClick(sku)}
                                />
                            </li>
                        );
                    })}
                </ul>
                <div css={styles.footer}>
                    <PriceTotal
                        currentProduct={currentSku}
                        bundleProducts={selectedBundleProducts}
                    />
                    <AddAllButton
                        disabled={!hasSelection}
                        selectedItems={selectedBundleProducts}
                    />
                </div>
            </div>
        </section>
    );
};

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: space[4],
        marginTop: space[4],
        [mediaQueries.sm]: {
            marginTop: space[5]
        }
    },
    widgetCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[3],
        width: '100%',
        border: `${borders[1]} ${colors.lightGray}`,
        borderRadius: radii[3],
        overflow: 'hidden',
        paddingBottom: space[4],
        [mediaQueries.sm]: {
            flexDirection: 'row',
            padding: space[5],
            gap: 0
        }
    },
    productsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        listStyle: 'none',
        padding: 0,
        margin: 0,
        [mediaQueries.sm]: {
            flex: 1,
            minWidth: 0,
            flexDirection: 'row',
            overflowX: 'auto',
            gap: space[5],
            paddingBottom: space[4],
            '& > li': {
                flex: 1,
                boxSizing: 'border-box'
            },
            '& > li:not(:first-of-type)': {
                borderLeft: `${borders[1]} ${colors.lightGray}`,
                paddingLeft: space[5]
            }
        }
    },
    productsWrapperCarousel: {
        [mediaQueries.sm]: {
            '& > li': {
                flex: 'none',
                flexShrink: 0,
                width: CAROUSEL_CARD_WIDTH
            }
        }
    },
    footer: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[4],
        padding: `0 ${space[4]}px`,
        width: '100%',
        [mediaQueries.sm]: {
            padding: 0,
            paddingLeft: space[6],
            width: 'auto',
            justifyContent: 'center',
            borderLeft: `${borders[1]} ${colors.lightGray}`
        }
    }
};

export default wrapFunctionalComponent(BundleWidget, 'BundleWidget');
